// Lib
import Immutable from 'immutable';

// FIXME send the previous scale in from the consumer rather than using a singleton?
// Singleton
import dragAndDropStateSingleton from '../../../element/dnd/dragAndDropStateSingleton';

// Utils
import * as pointLib from '../../../../common/maths/geometry/point';
import { getDomElementId } from '../../../element/utils/elementUtil';
import { isIconViewLike } from '../../../../common/elements/utils/elementDisplayUtils';
import { getIsCollapsed } from '../../../../common/elements/utils/elementPropertyUtils';
import { displayCommentAsCollapsed } from '../../../../common/comments/commentModelUtil';
import { getInterpolatedState } from './elementDragLayerStateManagerInterpolationUtils';
import { isAnnotation, isCommentThread, isLine } from '../../../../common/elements/utils/elementTypeUtils';
import { isLocationInbox } from '../../../../common/elements/utils/elementLocationUtils';

// Constants
import { MODIFIER_KEYS } from '../../../utils/dnd/modifierKeys/dragModifierKeysConstants';

// Types
import { Point } from '../../../../common/maths/geometry/pointTypes';
import { MNElement } from '../../../../common/elements/elementModelTypes';
import {
    DragAndDropInterpolationStyle,
    DragAndDropMode,
    DragAndDropSnapMode,
    DragAndDropState,
} from '../dragLayerStateTypes';

const THRESHOLD = 50;

/**
 * Determine if the drag should be limited to right angles.
 */
const shouldLimitDragToRightAngles = (dragModifierKeys: Immutable.Map<string, boolean>) =>
    dragModifierKeys.get(MODIFIER_KEYS.shiftKey);

/**
 * Determine the snap mode based on the modifier keys and the previous snap mode.
 *
 * This method uses the previous snap mode to make the snapping sticky, so that if you're snapping
 * horizontally it doesn't snap vertically unless you move past the threshold, or vice versa.
 *
 * SNAP_HORIZONTAL: The x dimension remains constant, so the element will only be dragged vertically
 * SNAP_VERTICAL: The y dimension remains constant, so the element will only be dragged horizontally
 */
const getDragAndDropSnapMode = (
    element: MNElement,
    dragModifierKeys: Immutable.Map<string, boolean>,
    previousSnapMode: DragAndDropSnapMode,
    movementOffset: Point,
) => {
    // Don't try to snap to right angles if the element is within the unsorted notes or a column
    if (isLocationInbox(element)) return DragAndDropSnapMode.NONE;

    if (!shouldLimitDragToRightAngles(dragModifierKeys)) return DragAndDropSnapMode.NONE;

    // Make the snapping sticky, so that if you're snapping horizontally it doesn't snap vertically
    //  until you move past the threshold, or vice versa
    if (
        previousSnapMode === DragAndDropSnapMode.SNAP_HORIZONTAL &&
        Math.abs(movementOffset.x) - THRESHOLD < Math.abs(movementOffset.y)
    ) {
        return DragAndDropSnapMode.SNAP_HORIZONTAL;
    }

    if (
        previousSnapMode === DragAndDropSnapMode.SNAP_VERTICAL &&
        Math.abs(movementOffset.y) - THRESHOLD < Math.abs(movementOffset.x)
    ) {
        return DragAndDropSnapMode.SNAP_VERTICAL;
    }

    return Math.abs(movementOffset.x) > Math.abs(movementOffset.y)
        ? DragAndDropSnapMode.SNAP_VERTICAL
        : DragAndDropSnapMode.SNAP_HORIZONTAL;
};

/**
 * Gets the source client offset with the snap applied.
 */
const getSnappedSourceClientOffset = (
    snapMode: DragAndDropSnapMode,
    sourceClientOffset: Point,
    movementOffset: Point,
): Point => {
    if (snapMode === DragAndDropSnapMode.NONE) return sourceClientOffset;

    // When snapping horizontally the x dimension should remain constant, so we subtract the movement offset
    if (snapMode === DragAndDropSnapMode.SNAP_HORIZONTAL) {
        return {
            x: sourceClientOffset.x - movementOffset.x,
            y: sourceClientOffset.y,
        };
    }

    return {
        x: sourceClientOffset.x,
        y: sourceClientOffset.y - movementOffset.y,
    };
};

/**
 * Determines the drag and drop mode based on the hovered element.
 */
export const getDragAndDropMode = (attachModeHoveredId?: string): DragAndDropMode => {
    if (attachModeHoveredId) return DragAndDropMode.ATTACH;

    return DragAndDropMode.DEFAULT;
};

/**
 * Determines whether to apply 2D, 3D or no interpolation.
 */
export const getDragAndDropInterpolationStyle = (
    element: MNElement,
    draggedElementCount: number,
    dragAndDropMode: DragAndDropMode,
    areDnd3dEffectsEnabled: boolean,
): DragAndDropInterpolationStyle => {
    if (dragAndDropMode === DragAndDropMode.ATTACH) return DragAndDropInterpolationStyle.NONE;
    if (isCommentThread(element) && getIsCollapsed(element)) return DragAndDropInterpolationStyle.NONE;

    if (!areDnd3dEffectsEnabled) return DragAndDropInterpolationStyle.TWO_D;
    if (draggedElementCount > 1) return DragAndDropInterpolationStyle.TWO_D;
    if (isIconViewLike(element)) return DragAndDropInterpolationStyle.TWO_D;
    if (isLine(element)) return DragAndDropInterpolationStyle.TWO_D;
    if (isAnnotation(element)) return DragAndDropInterpolationStyle.TWO_D;

    return DragAndDropInterpolationStyle.THREE_D;
};

/**
 * Depending on how the attach mode was entered, we need to account for changes in the grab position / mouse
 * position relationship.
 */
const getAttachedDragOffset = (
    element: MNElement,
    isNewElement: boolean,
    sourceClientOffset: Point,
    clientOffset: Point,
): Point => {
    if (isNewElement) return clientOffset;

    // If the comment switches from expanded to collapsed, we need to account for the grab offset
    const shiftByGrabOffset = !displayCommentAsCollapsed(element);

    if (shiftByGrabOffset && dragAndDropStateSingleton.scaledGrabOffset)
        return pointLib.translate(dragAndDropStateSingleton.scaledGrabOffset)(sourceClientOffset);

    return sourceClientOffset;
};

/**
 * Determines the new group position when in attach mode.
 */
const getAttachDragAndDropGroupPosition = (
    element: MNElement,
    isNewElement: boolean,
    sourceClientOffset: Point,
    clientOffset: Point,
    attachModeHoveredId?: string,
): Point => {
    const offset = getAttachedDragOffset(element, isNewElement, sourceClientOffset, clientOffset);

    const hoveredDomNode = document.getElementById(getDomElementId(attachModeHoveredId));

    if (!hoveredDomNode) return sourceClientOffset;

    const boundingRect = hoveredDomNode.getBoundingClientRect();

    const translation = { x: 0, y: 0 };

    if (boundingRect.top > offset.y) {
        translation.y = boundingRect.top - offset.y;
    } else if (boundingRect.bottom < offset.y) {
        translation.y = boundingRect.bottom - offset.y;
    }

    if (boundingRect.left > offset.x) {
        translation.x = boundingRect.left - offset.x;
    } else if (boundingRect.right < offset.x) {
        translation.x = boundingRect.right - offset.x;
    }

    // @ts-ignore TS can't deal with currying in JS files
    return pointLib.translate(sourceClientOffset, translation);
};

/**
 * Adjusts the source client offset based on the drag and drop mode.
 */
const getLimitedSourceClientOffset = (
    snappedSourceClientOffset: Point,
    dragAndDropMode: DragAndDropMode,
    element: MNElement,
    isNewElement: boolean,
    clientOffset: Point,
    attachModeHoveredId?: string,
) => {
    if (dragAndDropMode === DragAndDropMode.ATTACH) {
        return getAttachDragAndDropGroupPosition(
            element,
            isNewElement,
            snappedSourceClientOffset,
            clientOffset,
            attachModeHoveredId,
        );
    }

    return snappedSourceClientOffset;
};

/**
 * Account for changes in scroll when positioning the dragged element.
 */
const getMovementOffsetAfterScroll = (
    initialCanvasScroll: Point,
    currentCanvasScroll: Point,
    movementOffset: Point,
) => {
    // @ts-ignore TS can't deal with currying in JS files
    const scrollDiff = pointLib.difference(currentCanvasScroll, initialCanvasScroll);
    // @ts-ignore TS can't deal with currying in JS files
    return pointLib.difference(scrollDiff, movementOffset);
};

export const getUpdatedElementDragLayerData = (
    previousState: DragAndDropState,
    dt: number,

    initialCanvasScroll: Point,
    currentCanvasScroll: Point,

    element: MNElement,
    isNewElement: boolean,
    draggedElements: MNElement[],
    sourceClientOffset: Point,
    scaledCustomDragOffset: Point,

    movementOffset: Point,
    dragModifierKeys: Immutable.Map<string, boolean>,
    clientOffset: Point,
    areDnd3dEffectsEnabled: boolean,
    isTouchDevice: boolean,
    attachModeHoveredId?: string,
) => {
    const adjustedMovementOffset = getMovementOffsetAfterScroll(
        initialCanvasScroll,
        currentCanvasScroll,
        movementOffset,
    );

    const dragAndDropMode = getDragAndDropMode(attachModeHoveredId);
    const snapMode = getDragAndDropSnapMode(element, dragModifierKeys, previousState.snapMode, adjustedMovementOffset);

    const snappedSourceClientOffset = getSnappedSourceClientOffset(
        snapMode,
        sourceClientOffset,
        adjustedMovementOffset,
    );

    const limitedSourceClientOffset = getLimitedSourceClientOffset(
        snappedSourceClientOffset,
        dragAndDropMode,
        element,
        isNewElement,
        clientOffset,
        attachModeHoveredId,
    );

    const dragAndDropInterpolationStyle = getDragAndDropInterpolationStyle(
        element,
        draggedElements.length,
        dragAndDropMode,
        areDnd3dEffectsEnabled,
    );
    const interpolatedState = getInterpolatedState(
        dragAndDropInterpolationStyle,
        previousState,
        element,
        limitedSourceClientOffset,
        adjustedMovementOffset,
        scaledCustomDragOffset,
        isTouchDevice,
        dt,
    );

    return {
        dragAndDropMode,
        snapMode,
        interpolationStyle: dragAndDropInterpolationStyle,
        ...interpolatedState,
    };
};
